import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { useRef, useCallback, useEffect } from 'react';
import { track } from '../../analytics/AnalyticsService.js';
import 'branch-sdk';
import '../../analytics/consent.js';
import { AnalyticsTrackingEvent } from '../../analytics/events.js';
import '../../analytics/externalTracking.js';
import { tryGetVisitorId } from '../../analytics/fingerprint.js';
import '../../analytics/types.js';
import '../../analytics/metaPixel.js';
import { getPageNameFromPath, getUTMParams, getDeviceType } from '../tracking/ConciergeTrackingTypes.js';

const SESSION_TRACKED_KEY = 'concierge_session_tracked';
/**
 * Hook to track WIDGET_IMPRESSION and WIDGET_SESSION_START events for the concierge.
 *
 * - WIDGET_IMPRESSION: Fires when the widget becomes visible (uses IntersectionObserver)
 * - WIDGET_SESSION_START: Fires ONCE per session when user first sees the widget
 *
 * @returns A ref callback to attach to the widget element for visibility tracking
 */
function useWidgetVisibilityTracking(options = {}) {
    const { appName = '', widgetType = 'floating_button', enabled = true } = options;
    // Track whether we've already fired impression for this component instance
    const hasTrackedImpression = useRef(false);
    const observerRef = useRef(null);
    const elementRef = useRef(null);
    /**
     * Check if session start has already been tracked in this browser session
     */
    const isSessionTracked = useCallback(() => {
        if (typeof window === 'undefined')
            return true;
        try {
            return sessionStorage.getItem(SESSION_TRACKED_KEY) === 'true';
        }
        catch (_a) {
            // sessionStorage might not be available (e.g., incognito mode in some browsers)
            return false;
        }
    }, []);
    /**
     * Mark session as tracked
     */
    const markSessionTracked = useCallback(() => {
        if (typeof window === 'undefined')
            return;
        try {
            sessionStorage.setItem(SESSION_TRACKED_KEY, 'true');
        }
        catch (_a) {
            // Silently fail if sessionStorage is not available
        }
    }, []);
    /**
     * Track WIDGET_IMPRESSION event
     * Non-blocking - fire and forget
     */
    const trackWidgetImpression = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!enabled || hasTrackedImpression.current)
            return;
        hasTrackedImpression.current = true;
        const sessionId = (yield tryGetVisitorId()) || 'unknown';
        const properties = {
            pageUrl: window.location.href,
            pageName: getPageNameFromPath(window.location.pathname),
            widgetType,
            sessionId,
            appName: appName || 'unknown',
        };
        // Non-blocking - don't await
        track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_IMPRESSION, properties).catch((err) => {
            // Silently handle tracking errors - don't break the app
            console.debug('[Concierge Tracking] Failed to track widget impression:', err);
        });
    }), [enabled, widgetType, appName]);
    /**
     * Track WIDGET_SESSION_START event (once per session)
     * Non-blocking - fire and forget
     */
    const trackWidgetSessionStart = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!enabled || isSessionTracked())
            return;
        // Mark as tracked immediately to prevent race conditions
        markSessionTracked();
        const sessionId = (yield tryGetVisitorId()) || 'unknown';
        const utmParams = getUTMParams();
        const properties = Object.assign({ sessionId, appName: appName || 'unknown', referrer: document.referrer || '', deviceType: getDeviceType(), pageUrl: window.location.href }, utmParams);
        // Non-blocking - don't await
        track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_SESSION_START, properties).catch((err) => {
            // Silently handle tracking errors - don't break the app
            console.debug('[Concierge Tracking] Failed to track widget session start:', err);
        });
    }), [enabled, appName, isSessionTracked, markSessionTracked]);
    /**
     * Set up IntersectionObserver to track when widget becomes visible
     */
    useEffect(() => {
        if (!enabled || typeof window === 'undefined')
            return;
        // Clean up existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        observerRef.current = new IntersectionObserver((entries) => {
            var _a;
            const entry = entries[0];
            if (entry === null || entry === void 0 ? void 0 : entry.isIntersecting) {
                // Widget is visible - track both events
                trackWidgetImpression();
                trackWidgetSessionStart();
                // Disconnect observer after firing - we only need to track once per page load
                (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            }
        }, {
            // Fire when at least 50% of the element is visible
            threshold: 0.5,
        });
        // Observe the element if it's already set
        if (elementRef.current) {
            observerRef.current.observe(elementRef.current);
        }
        return () => {
            var _a;
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        };
    }, [enabled, trackWidgetImpression, trackWidgetSessionStart]);
    /**
     * Ref callback to attach to the widget element
     * This handles both initial render and subsequent updates
     */
    const setTrackingRef = useCallback((element) => {
        elementRef.current = element;
        if (element && observerRef.current) {
            observerRef.current.observe(element);
        }
    }, []);
    /**
     * Manual trigger for tracking (useful for components that don't use the ref)
     * Tracks impression immediately without waiting for intersection
     */
    const trackImmediate = useCallback(() => {
        trackWidgetImpression();
        trackWidgetSessionStart();
    }, [trackWidgetImpression, trackWidgetSessionStart]);
    return {
        /** Ref callback to attach to the widget element for visibility-based tracking */
        setTrackingRef,
        /** Manually trigger tracking immediately (bypasses IntersectionObserver) */
        trackImmediate,
    };
}

export { useWidgetVisibilityTracking };
//# sourceMappingURL=useWidgetVisibilityTracking.js.map
